import { find } from "lodash";

interface iHomepageRouteOption {
  label: string;
  route: string;
}

/**
 * Controller for the admin homepage settings page
 *
 * @returns {void}
 */
function adminHomepageSettingsController(
  $scope,
  siteSettingsPayload,
  SettingsApi,
  UsersApi,
  NewsApi,
  PromotionsApi,
  ResourcesApi,
  MemberPackagesApi,
  EventsApi,
  PollsApi,
  $q,
  UploadsApi,
  siteSettingsManager,
  $uibModal,
  alerts,
) {
  // properties
  const vm = this;
  vm.loadingConfig = {
    show: false,
    message: "Loading...",
    progress: null,
  };
  vm.settings = angular.copy(siteSettingsPayload);
  vm.featuredMember = null;
  vm.featuredNews = null;
  vm.featuredPromotion = null;
  vm.featuredResource = null;
  vm.featuredPoll = null;
  vm.formErrors = null;
  // functions in here (must return a $q.promise) will be run before saving the settings
  vm.beforeSaveFunctions = {
    transformColors,
  };

  vm.logoUploadOptions = {
    resize: true,
    resizeSize: {
      w: 360,
      h: 100,
    },
    resizeShape: "rectangle",
    resizeAspectRatio: 3.6,
  };
  vm.faviconUploadOptions = {
    resize: true,
    resizeSize: {
      w: 32,
      h: 32,
    },
    resizeShape: "square",
    resizeAspectRatio: 1.1,
  };
  vm.pwaIconUploadOptions = {
    resize: true,
    resizeSize: {
      w: 144,
      h: 144,
    },
    resizeShape: "square",
    resizeAspectRatio: 1.1,
  };
  vm.coverImageUploadOptions = {
    resize: true,
    resizeSize: {
      w: 1920,
      h: 480,
    },
    resizeShape: "rectangle",
    resizeAspectRatio: 4,
  };
  vm.minicolorOptions = {
    position: "top left",
    letterCase: "uppercase",
  };
  vm.linksTemplate = {
    link: {
      label: "Link",
      type: "string",
      placeholder: "http://",
    },
    label: {
      label: "Title",
      type: "string",
      placeholder: "Link text",
    },
  };
  vm.homepageRouteOptions = [
    {
      label: "Default Homepage",
      route: "homepage",
    },
    {
      label: "Events",
      route: "events.index",
    },
    {
      label: "Forum",
      route: "forum.index",
    },
    {
      label: "Members Directory",
      route: "members.index",
    },
    {
      label: "News",
      route: "news.index",
    },
  ] as iHomepageRouteOption[];

  // methods
  vm.getSelectedHomepageRouteOption = getSelectedHomepageRouteOption;
  vm.handleBodyContentChange = handleBodyContentChange;
  vm.handleCoverImageSelect = handleCoverImageSelect;
  vm.handleFaviconSelect = handleFaviconSelect;
  vm.handlePwaIconSelect = handlePwaIconSelect;
  vm.handleHomepageRouteNameChange = handleHomepageRouteNameChange;
  vm.handleLogoSelect = handleLogoSelect;
  vm.pickFeaturedMember = pickFeaturedMember;
  vm.pickFeaturedNews = pickFeaturedNews;
  vm.pickFeaturedResource = pickFeaturedResource;
  vm.pickFeaturedPromotion = pickFeaturedPromotion;
  vm.pickFeaturedPoll = pickFeaturedPoll;
  vm.pickFeaturedPackage = pickFeaturedPackage;
  vm.pickFeaturedEvent = pickFeaturedEvent;
  vm.removeFeaturedResource = removeFeaturedResource;
  vm.removeFavicon = removeFavicon;
  vm.removePwaIcon = removePwaIcon;
  vm.removeLogo = removeLogo;
  vm.removeCoverImage = removeCoverImage;
  vm.saveSettings = saveSettings;

  /**
   * Initiates this controller
   */
  function init() {
    // get featured member
    if (vm.settings.featured_member_id) {
      getFeaturedMember(vm.settings.featured_member_id);
    }
    // get featured event
    if (vm.settings.featured_event_id) {
      getFeaturedEvent(vm.settings.featured_event_id);
    }
    // get featured news
    if (vm.settings.featured_news_id) {
      getFeaturedNews(vm.settings.featured_news_id);
    }
    // get featured promotion
    if (vm.settings.featured_promotion_id) {
      getFeaturedPromotion(vm.settings.featured_promotion_id);
    }
    // get featured resource
    if (vm.settings.featured_resource_id) {
      getFeaturedResource(vm.settings.featured_resource_id);
    }
    // get featured poll
    if (vm.settings.featured_poll_id) {
      getFeaturedPoll(vm.settings.featured_poll_id);
    }
    // get featured package
    if (vm.settings.featured_package_id) {
      getFeaturedPackage(vm.settings.featured_package_id);
    }
  }

  /**
   * Retrieves the featured member from the API and adds to scope for rendering
   * @param  {int} id - ID of member
   * @return {void}
   */
  function getFeaturedMember(id) {
    UsersApi.get(
      {
        id,
      },
      (response) => {
        vm.featuredMember = response.data;
      },
    );
  }

  /**
   * Gets the selected option from vm.homepageRouteOptions
   *
   * @returns iHomepageRouteOption
   */
  function getSelectedHomepageRouteOption(): iHomepageRouteOption {
    return find(vm.homepageRouteOptions, {
      route: vm.settings.homepage_route_name || "homepage",
    });
  }

  /**
   * Updates the body content.
   *
   * @param {string} content html
   */
  function handleBodyContentChange(content) {
    vm.settings.homepage_body_content = content;
  }

  /**
   * Shows modal that lets the admin search and pick a featured member
   * @return {void}
   */
  function pickFeaturedMember() {
    $uibModal
      .open({
        templateUrl: "modals/admin-pick-featured-member.html",
        controller: "pickFeaturedMemberController",
        controllerAs: "vm",
        windowClass: "pick-featured-member",
      })
      .result.then((featuredMember) => {
        vm.featuredMember = featuredMember;
        vm.settings.featured_member_id = featuredMember.id;
      });
  }
  /**
   * Retrieves the featured package post from the API and adds to scope for rendering
   * @param  {int} id - ID of news post
   * @return {void}
   */
  function getFeaturedPackage(id) {
    MemberPackagesApi.get(
      {
        id,
      },
      (response) => {
        vm.featuredPackage = response.data;
      },
    );
  }

  /**
   * Shows modal tat lets the admin search and pick a featured news post
   * @return {void}
   */
  function pickFeaturedPackage() {
    $uibModal
      .open({
        templateUrl: "modals/admin-pick-featured-package.html",
        controller: "pickFeaturedPackageController",
        controllerAs: "vm",
        windowClass: "pick-featured-package",
      })
      .result.then((featuredPackage) => {
        vm.featuredPackage = featuredPackage;
        vm.settings.featured_package_id = featuredPackage.id;
      });
  }
  /**
   * Retrieves the featured news post from the API and adds to scope for rendering
   * @param  {int} id - ID of news post
   * @return {void}
   */
  function getFeaturedEvent(id) {
    EventsApi.get(
      {
        id,
      },
      (response) => {
        vm.featuredEvent = response.data;
      },
    );
  }

  /**
   * Shows modal tat lets the admin search and pick a featured news post
   * @return {void}
   */
  function pickFeaturedEvent() {
    $uibModal
      .open({
        templateUrl: "modals/admin-pick-featured-event.html",
        controller: "pickFeaturedEventController",
        controllerAs: "vm",
        windowClass: "pick-featured-event",
      })
      .result.then((featuredEvent) => {
        vm.featuredEvent = featuredEvent;
        vm.settings.featured_event_id = featuredEvent.id;
      });
  }
  /**
   * Retrieves the featured news post from the API and adds to scope for rendering
   * @param  {int} id - ID of news post
   * @return {void}
   */
  function getFeaturedNews(id) {
    NewsApi.get(
      {
        id,
      },
      (response) => {
        vm.featuredNews = response.data;
      },
    );
  }

  /**
   * Shows modal tat lets the admin search and pick a featured news post
   * @return {void}
   */
  function pickFeaturedNews() {
    $uibModal
      .open({
        templateUrl: "modals/admin-pick-featured-news.html",
        controller: "pickFeaturedNewsController",
        controllerAs: "vm",
        windowClass: "pick-featured-news",
      })
      .result.then((featuredNews) => {
        vm.featuredNews = featuredNews;
        vm.settings.featured_news_id = featuredNews.id;
      });
  }

  /**
   * Retrieves the featured pormotion post from the API and adds to scope for rendering
   * @param  {int} id - ID of pormotion post
   * @return {void}
   */
  function getFeaturedResource(id) {
    PromotionsApi.get(
      {
        id,
      },
      (response) => {
        vm.featuredResource = response.data;
      },
    );
  }

  /**
   * Shows modal tat lets the admin search and pick a featured news post
   * @return {void}
   */
  function pickFeaturedResource() {
    $uibModal
      .open({
        templateUrl: "modals/admin-pick-featured-resource.html",
        controller: "pickFeaturedResourceController",
        controllerAs: "vm",
        windowClass: "pick-featured-Resource",
      })
      .result.then((featuredResource) => {
        vm.featuredResource = featuredResource;
        vm.settings.featured_resource_id = featuredResource.id;
      });
  }
  /**
   * Removes the currently selected featured resource and sets it to null
   * @return {void}
   */
  function removeFeaturedResource() {
    vm.featuredResource = null;
    vm.settings.featured_resource_id = null;
  }

  /**
   * Retrieves the featured pormotion post from the API and adds to scope for rendering
   * @param  {int} id - ID of pormotion post
   * @return {void}
   */
  function getFeaturedPromotion(id) {
    PromotionsApi.get(
      {
        id,
      },
      (response) => {
        vm.featuredPromotion = response.data;
      },
    );
  }

  /**
   * Shows modal tat lets the admin search and pick a featured news post
   * @return {void}
   */
  function pickFeaturedPromotion() {
    $uibModal
      .open({
        templateUrl: "modals/admin-pick-featured-promotion.html",
        controller: "pickFeaturedPromotionController",
        controllerAs: "vm",
        windowClass: "pick-featured-promotion",
      })
      .result.then((featuredPromotion) => {
        vm.featuredPromotion = featuredPromotion;
        vm.settings.featured_promotion_id = featuredPromotion.id;
      });
  }
  /**
   * Retrieves the featured package post from the API and adds to scope for rendering
   * @param  {int} id - ID of news post
   * @return {void}
   */
  function getFeaturedPoll(id) {
    PollsApi.get(
      {
        id,
      },
      (response) => {
        vm.featuredPoll = response.data;
      },
    );
  }

  /**
   * Shows modal tat lets the admin search and pick a featured poll
   * @return {void}
   */
  function pickFeaturedPoll() {
    $uibModal
      .open({
        templateUrl: "modals/admin-pick-featured-poll.html",
        controller: "pickFeaturedPollController",
        controllerAs: "vm",
        windowClass: "pick-featured-poll",
      })
      .result.then((featuredPoll) => {
        vm.featuredPoll = featuredPoll;
        vm.settings.featured_poll_id = featuredPoll.id;
      });
  }

  /**
   * Assigns chosen logo to the scope
   *
   * @param  {string} base64FileString - base64 fle
   * @return {void}
   */
  function handleLogoSelect(base64FileString) {
    if (!base64FileString) {
      return false;
    }

    // store image to update view
    vm.settings.site_logo_url = base64FileString;

    // prepare function that will upload the logo on save
    vm.beforeSaveFunctions.uploadLogo = handleLogoUpload;
  }

  /**
   * Uploads the chosen logo to the API and updates site settings with the new path
   *
   * @return {$q.promise}
   */
  function handleLogoUpload() {
    const deferred = $q.defer();
    const handleUploadLogoResponse = (response) => {
      vm.settings.site_logo = response.path;
      deferred.resolve();
    };
    const handleUploadLogoError = () => {
      deferred.reject();
    };
    const handleUploadLogoProgress = (progress) => {
      vm.loadingConfig.progress = progress;

      if (progress === 100) {
        vm.loadingConfig.progress = 0;
        vm.loadingConfig.message = "Loading...";
      }
    };

    vm.loadingConfig.message = "Uploading logo...";
    UploadsApi.uploadImage(vm.settings.site_logo_url, "site").then(
      handleUploadLogoResponse,
      handleUploadLogoError,
      handleUploadLogoProgress,
    );

    return deferred.promise;
  }

  /**
   * Clears the current logo
   *
   * @return {void}
   */
  function removeLogo() {
    vm.settings.site_logo = null;
    delete vm.beforeSaveFunctions.uploadLogo;
  }

  /**
   * Assigns chosen Favicon to the scope
   *
   * @param  {string} base64FileString - base64 fle
   * @return {void}
   */
  function handleFaviconSelect(base64FileString) {
    if (!base64FileString) {
      return false;
    }

    // store image to update view
    vm.settings.site_favicon_url = base64FileString;

    // prepare function that will upload the favicon on save
    vm.beforeSaveFunctions.uploadFavicon = handleFaviconUpload;
  }

  /**
   * Uploads the chosen favicon to the API and updates site settings with the new path
   *
   * @return {$q.promise}
   */
  function handleFaviconUpload() {
    const deferred = $q.defer();
    const handleUploadFaviconResponse = (response) => {
      vm.settings.site_favicon = response.path;
      deferred.resolve();
    };
    const handleUploadFaviconError = () => {
      deferred.reject();
    };
    const handleUploadFaviconProgress = (progress) => {
      vm.loadingConfig.progress = progress;

      if (progress === 100) {
        vm.loadingConfig.progress = 0;
        vm.loadingConfig.message = "Loading...";
      }
    };

    vm.loadingConfig.message = "Uploading logo...";
    UploadsApi.uploadImage(vm.settings.site_favicon_url, "site").then(
      handleUploadFaviconResponse,
      handleUploadFaviconError,
      handleUploadFaviconProgress,
    );

    return deferred.promise;
  }

  /**
   * Clears the current favicon
   *
   * @return {void}
   */
  function removeFavicon() {
    vm.settings.site_favicon = null;
    delete vm.beforeSaveFunctions.uploadFavicon;
  }

  /**
   * Assigns chosen PWA icon to the scope
   *
   * @param  {string} base64FileString - base64 fle
   * @return {void}
   */
  function handlePwaIconSelect(base64FileString) {
    if (!base64FileString) {
      return false;
    }

    // store image to update view
    vm.settings.pwa.icon_url = base64FileString;

    // prepare function that will upload the favicon on save
    vm.beforeSaveFunctions.uploadPwaIcon = handlePwaIconUpload;
  }

  /**
   * Uploads the chosen pwa icon to the API and updates site settings with the new path
   *
   * @return {$q.promise}
   */
  function handlePwaIconUpload() {
    const deferred = $q.defer();
    const handleUploadPwaIconResponse = (response) => {
      vm.settings.pwa.icon = response.path;
      deferred.resolve();
    };
    const handleUploadPwaIconError = () => {
      deferred.reject();
    };
    const handleUploadPwaIconProgress = (progress) => {
      vm.loadingConfig.progress = progress;

      if (progress === 100) {
        vm.loadingConfig.progress = 0;
        vm.loadingConfig.message = "Loading...";
      }
    };

    vm.loadingConfig.message = "Uploading logo...";
    UploadsApi.uploadImage(vm.settings.pwa.icon_url, "site").then(
      handleUploadPwaIconResponse,
      handleUploadPwaIconError,
      handleUploadPwaIconProgress,
    );

    return deferred.promise;
  }

  /**
   * Clears the current favicon
   *
   * @return {void}
   */
  function removePwaIcon() {
    vm.settings.pwa.icon = null;
    delete vm.beforeSaveFunctions.uploadPwaIcon;
  }

  /**
   * Assigns chosen cover image to the scope
   * @param  {string} base64File - base64 file
   * @return {void}
   */
  function handleCoverImageSelect(base64File) {
    if (!base64File) {
      return false;
    }

    // save file so the view updates
    vm.settings.site_cover_image_url = base64File;
    // prepare to upload the file when settings are saved
    vm.beforeSaveFunctions.uploadCoverImage = handleCoverImageUpload;
  }

  /**
   * Uploads the cover image to the API and saves the new path to vm.settings
   *
   * @return {$q.promise}
   */
  function handleCoverImageUpload() {
    const deferred = $q.defer();
    const handleCoverImageUploadSuccess = (response) => {
      vm.settings.site_cover_image = response.path;
      deferred.resolve();
    };
    const handleCoverImageUploadError = () => {
      deferred.reject();
    };
    const handleCoverImageUploadProgress = (progress) => {
      vm.loadingConfig.progress = progress;
      if (progress === 100) {
        vm.loadingConfig.progress = 0;
      }
    };

    vm.loadingConfig.message = "Uploading cover image...";
    UploadsApi.uploadImage(vm.settings.site_cover_image_url, "site").then(
      handleCoverImageUploadSuccess,
      handleCoverImageUploadError,
      handleCoverImageUploadProgress,
    );

    return deferred.promise;
  }

  /**
   * Sets the value of the homepage route when the <Select> is changed.
   *
   * @param {iHomepageRouteOption} option
   * @returns {void}
   */
  function handleHomepageRouteNameChange(option: iHomepageRouteOption): void {
    vm.settings.homepage_route_name = option.route;
    $scope.$apply(); // damn angular just needs a little kick
  }

  /**
   * Clears the current cover image
   * @return {void}
   */
  function removeCoverImage() {
    vm.settings.site_cover_image = null;
  }

  /**
   * Transforms colours to the correct format before they're saved to the API
   *
   * @returns {$q.promise}
   */
  function transformColors() {
    const deferred = $q.defer();
    vm.loadingConfig.message = "Transforming site colours...";

    siteSettingsManager.transformColorValues(vm.settings);
    deferred.resolve();

    return deferred.promise;
  }

  /**
   * Saves the site settings
   *
   * @returns {void}
   */
  function saveSettings() {
    vm.loadingConfig.show = true;
    const handleSaveResponse = (response) => {
      // update settings
      vm.settings = response.data;
      // reset promises
      vm.beforeSaveFunctions = {
        transformColors,
      };
      // stop loading UI
      vm.loadingConfig.show = false;
      // show alert
      alerts.emit({
        theme: "success",
        title: "Saved",
        message: "Settings",
      });
    };
    const handleSaveError = () => {
      vm.loadingConfig.show = false;
    };
    const handleSave = () => {
      vm.loadingConfig.message = "Saving...";

      SettingsApi.save(
        {
          settings: vm.settings,
        },
        handleSaveResponse,
        handleSaveError,
      );
    };

    // create array of functions to run before saving
    const beforeSavePromises = Object.values(vm.beforeSaveFunctions).map(
      (func: () => Promise<any>) => func(),
    );

    $q.all(beforeSavePromises).then(handleSave);
  }

  init();
}
adminHomepageSettingsController.$inject = [
  "$scope",
  "siteSettingsPayload",
  "SettingsApi",
  "UsersApi",
  "NewsApi",
  "PromotionsApi",
  "ResourcesApi",
  "MemberPackagesApi",
  "EventsApi",
  "PollsApi",
  "$q",
  "UploadsApi",
  "siteSettingsManager",
  "$uibModal",
  "alerts",
];

angular
  .module("app")
  .controller(
    "adminHomepageSettingsController",
    adminHomepageSettingsController,
  );
